/**
 * Cloud storage URL utilities
 *
 * Converts cloud storage URIs (s3://, gs://) to their respective console URLs
 */

/**
 * Converts a cloud storage URI to its console URL
 *
 * @param uri - Cloud storage URI (e.g., s3://bucket/path or gs://bucket/path)
 * @returns Console URL for the cloud storage resource, or null if not a supported cloud URI
 *
 * @example
 * ```typescript
 * getCloudConsoleUrl('s3://my-bucket/path/file.txt')
 * // Returns "https://s3.console.aws.amazon.com/s3/buckets/my-bucket?prefix=path%2Ffile.txt/"
 *
 * getCloudConsoleUrl('gs://my-bucket/path/file.txt')
 * // Returns "https://console.cloud.google.com/storage/browser/my-bucket/path/file.txt"
 *
 * getCloudConsoleUrl('http://example.com')
 * // Returns null
 * ```
 */
export const getCloudConsoleUrl = (uri: string): string | null => {
  if (uri.startsWith('s3://')) {
    const path = uri.slice(5);
    const [bucket, ...rest] = path.split('/');
    const prefix = rest.join('/');
    if (prefix) {
      return `https://s3.console.aws.amazon.com/s3/buckets/${bucket}?prefix=${encodeURIComponent(prefix)}/`;
    }

    return `https://s3.console.aws.amazon.com/s3/buckets/${bucket}`;
  }

  if (uri.startsWith('gs://')) {
    const path = uri.slice(5);
    const [bucket, ...rest] = path.split('/');
    const prefix = rest.join('/');
    if (prefix) {
      return `https://console.cloud.google.com/storage/browser/${bucket}/${prefix}`;
    }

    return `https://console.cloud.google.com/storage/browser/${bucket}`;
  }

  return null;
};
